import { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CATEGORY_PROPERTIES } from '@/api/queries/categories.queries';
import { GetCategoryPropertiesResponse } from '@/api/types/categories.types';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { usePageType } from '@/hooks/usePageType/usePageType';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import {
  AdSubmissionStatusEnum,
  CreateAdSubmissionResponse,
} from '@/api/types/ads.types';
import { CREATE_AD_SUBMISSION } from '@/api/mutations/ads.mutations';
import { getFlowId } from '@/hooks/useAdSubmissionFlow/useAdSubmissionFlow.helpers';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import { ROUTES } from '@/constants/routes';

export const useCategoryProperties = (
  categoryPath?: string,
  hasDefaultSubcategory?: boolean
) => {
  const { currentCategory } = useCurrentCategory();
  const pageType = usePageType();
  const source = usePlatformSource();
  const path = categoryPath || currentCategory?.path;

  const [sendCategorySelect] =
    useMutation<CreateAdSubmissionResponse>(CREATE_AD_SUBMISSION);

  const handleCategorySelectStatus = useCallback(
    (previousData: GetCategoryPropertiesResponse | undefined) => {
      const flowId = getFlowId();

      if (
        typeof window === 'undefined' ||
        pageType !== PageType.NewAd ||
        (!previousData && hasDefaultSubcategory) ||
        !flowId ||
        path === ROUTES.ad.slice(1)
      ) {
        return;
      }

      sendCategorySelect({
        variables: {
          flowId,
          status: AdSubmissionStatusEnum.CategorySelect,
          source,
        },
      });
    },
    [pageType, hasDefaultSubcategory, path, sendCategorySelect, source]
  );

  const {
    data,
    refetch: refetchCategoryProperties,
    loading,
    previousData,
  } = useQuery<GetCategoryPropertiesResponse>(GET_CATEGORY_PROPERTIES, {
    skip: !path,
    variables: { path },
    errorPolicy: 'all',
    // Trigger CATEGORY_SELECT flow status once category data is resolved on new ad page
    onCompleted: () => {
      handleCategorySelectStatus(previousData);
    },
    onError: () => {
      if (data?.category?.id) {
        handleCategorySelectStatus(previousData);
      }
    },
  });

  const { id: categoryId, properties: categoryProperties } =
    data?.category ?? {};

  return {
    categoryId,
    categoryProperties,
    refetchCategoryProperties,
    isCategoriesLoading: loading,
  };
};
